import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { AlertCircle, Play, X, ArrowUpRight } from "lucide-react";

/**
 * Two-step "Generate previous month's payouts" flow:
 *   1. Fetch a dry-run preview (`?dry_run=true`) — no DB mutation
 *   2. Admin reviews the partner list + totals
 *   3. Clicks Confirm → real POST creates the payouts
 *
 * If the admin cancels, nothing has changed. Safe for demo-mode rehearsals
 * or for previewing the 1st-of-month scheduler run any day.
 */
export default function GenerateMonthlyModal({ onClose, onGenerated }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/commissions/payouts/generate-monthly?dry_run=true");
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onClose]);

  async function confirm() {
    setConfirming(true);
    try {
      const { data } = await api.post("/commissions/payouts/generate-monthly");
      const n = (data.generated || []).length;
      toast.success(
        n === 0
          ? `Nothing to generate for ${data.period}.`
          : `Generated ${n} payout${n === 1 ? "" : "s"} for ${data.period}. Head to Ledger to review.`
      );
      onGenerated?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generate failed");
    } finally {
      setConfirming(false);
    }
  }

  const period = preview?.period;
  const rows = preview?.generated || [];
  const skipped = preview?.skipped || [];
  const totalExGst = rows.reduce((s, r) => s + Number(r.amount_ex_gst || 0), 0);
  const totalEvents = rows.reduce((s, r) => s + Number(r.event_count || 0), 0);
  const topUps = rows.filter((r) => r.would_top_up_existing).length;

  return (
    <div
      data-testid="generate-monthly-modal"
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-800 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">Dry run</div>
            <h3 className="font-display text-2xl mt-1">
              Generate monthly payouts{period ? ` · ${period}` : ""}
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Preview what would be created. Nothing is written until you click Confirm.
            </p>
          </div>
          <button
            onClick={onClose}
            title="Close"
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="p-12 text-center text-sm text-slate-500">Computing preview…</div>
          )}

          {!loading && rows.length === 0 && (
            <div className="p-8">
              <div className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-950/40 p-4">
                <AlertCircle className="w-4 h-4 mt-0.5 text-amber-400 shrink-0" />
                <div>
                  <div className="font-medium text-slate-200">Nothing to generate for {period}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    No active partner has un-attributed accrued events in this period.
                    {skipped.length > 0 && ` ${skipped.length} partner${skipped.length === 1 ? "" : "s"} skipped.`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && rows.length > 0 && (
            <table className="w-full text-sm" data-testid="preview-table">
              <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold sticky top-0">
                <tr>
                  <th className="text-left px-6 py-3">Partner</th>
                  <th className="text-right px-6 py-3">Events</th>
                  <th className="text-right px-6 py-3">Ex-GST</th>
                  <th className="text-right px-6 py-3">GST</th>
                  <th className="text-right px-6 py-3">Payable</th>
                  <th className="text-left px-6 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((r) => (
                  <tr key={r.partner_code} data-testid={`preview-row-${r.partner_code}`}>
                    <td className="px-6 py-3">
                      <div className="font-medium">{r.partner_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.partner_code}</div>
                    </td>
                    <td className="px-6 py-3 text-right font-mono">{r.event_count}</td>
                    <td className="px-6 py-3 text-right font-mono">{money(r.amount_ex_gst)}</td>
                    <td className="px-6 py-3 text-right font-mono text-slate-400">{money(r.amount_gst)}</td>
                    <td className="px-6 py-3 text-right font-mono font-medium text-cyan-400">{money(r.amount_total)}</td>
                    <td className="px-6 py-3">
                      {r.would_top_up_existing ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[11px] font-semibold uppercase tracking-wider">
                          <ArrowUpRight className="w-3 h-3" /> Top-up
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-[11px] font-semibold uppercase tracking-wider">
                          New draft
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-950/40 border-t border-slate-800">
                  <td className="px-6 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Totals</td>
                  <td className="px-6 py-3 text-right font-mono">{totalEvents}</td>
                  <td className="px-6 py-3 text-right font-mono">{money(totalExGst)}</td>
                  <td className="px-6 py-3 text-right font-mono text-slate-400">{money(totalExGst * 0.1)}</td>
                  <td className="px-6 py-3 text-right font-mono font-semibold text-cyan-300">{money(totalExGst * 1.1)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}

          {!loading && skipped.length > 0 && rows.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-800 text-xs text-slate-500">
              {skipped.length} partner{skipped.length === 1 ? "" : "s"} skipped (no un-attributed accrued events).
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between gap-4">
          <div className="text-xs text-slate-500">
            {topUps > 0 && (
              <>
                <span className="text-amber-300 font-semibold">{topUps}</span> would top-up existing pending
                payout{topUps === 1 ? "" : "s"} · idempotent, no duplicates
              </>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-slate-800 text-sm hover:bg-slate-950"
            >
              Cancel
            </button>
            <button
              data-testid="generate-monthly-confirm"
              onClick={confirm}
              disabled={loading || confirming || rows.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-slate-950 text-sm font-semibold"
            >
              <Play className="w-3.5 h-3.5" />
              {confirming
                ? "Generating…"
                : rows.length === 0
                ? "Nothing to generate"
                : `Confirm & generate ${rows.length}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
